import React from 'react';
import {
  ModalPageView,
  useModalManager,
} from '@cotera/client/app/components/app/modal-manager/modal-manager';
import {
  AddWorkflowNodePicker,
  type AddWorkflowNodeConnecting,
} from './components/add-workflow-node-picker';
import type { WorkflowNodePaletteSelection } from './components/workflow-node-palette';
import {
  isDraftConnectUpstream,
  type DraftConnectFrom,
} from './workflow-draft-graph';

// A single searchable list, so a reading column rather than the tools-catalog
// dimensions: wide enough for a tool name plus its provider, capped at ~85vh.
const ADD_NODE_MODAL_SIZE = {
  custom: 'w-[92vw] max-w-xl max-h-[85dvh] rounded-lg',
} as const;

export type AddWorkflowNodeModalOptions = {
  /**
   * Whether the workflow can still accept a trigger (no enabled trigger bound
   * yet). Drives whether the "Trigger" option is offered on the landing page.
   */
  canAddTrigger: boolean;
  /** Hands off to the trigger editor drawer for the "add a trigger" flow. */
  onAddTrigger: () => void;
  /** Opens a new agent artifact from the picker's "Create new agent" row. */
  onCreateAgent: () => void;
  /** Opens a new workflow artifact from the picker's "Create new workflow" row. */
  onCreateWorkflow: () => void;
  /**
   * `loop-body` opens the same picker to choose the for-each child step
   * (agents / tools / flow control; no trigger).
   */
  intent?: 'add-node' | 'loop-body';
  /** Exclude from the run-workflow list (usually the workflow being edited). */
  excludeWorkflowId?: string;
};

/** Which way the picker's copy should read, given where the new step will land. */
function connectingFrom(
  connectFrom: DraftConnectFrom
): AddWorkflowNodeConnecting {
  if (isDraftConnectUpstream(connectFrom)) {
    return 'before';
  }
  if (connectFrom === undefined) {
    return null;
  }
  const hasTarget =
    typeof connectFrom === 'string'
      ? connectFrom !== ''
      : connectFrom.length > 0;
  return hasTarget ? 'after' : null;
}

/**
 * Opens the add-node modal: one searchable list of everything the builder can
 * add, sharing its options and ranking with the editor's `@` mention menu.
 */
export const useOpenAddWorkflowNodeModal = (
  onSelect: (
    selection: WorkflowNodePaletteSelection,
    connectFrom: DraftConnectFrom
  ) => void,
  defaultConnectFrom: DraftConnectFrom,
  options: AddWorkflowNodeModalOptions
) => {
  const modalManager = useModalManager((s) => s.actions);
  const {
    canAddTrigger,
    onAddTrigger,
    onCreateAgent,
    onCreateWorkflow,
    intent = 'add-node',
    excludeWorkflowId,
  } = options;

  return React.useCallback(
    (connectFromOverride?: DraftConnectFrom) => {
      const connectFrom = connectFromOverride ?? defaultConnectFrom;
      const connecting = connectingFrom(connectFrom);
      modalManager.push({
        size: ADD_NODE_MODAL_SIZE,
        view: () => (
          <ModalPageView className="flex min-h-0 flex-col px-6 py-6">
            <AddWorkflowNodePicker
              canAddTrigger={intent === 'loop-body' ? false : canAddTrigger}
              connecting={connecting}
              intent={intent}
              excludeWorkflowId={excludeWorkflowId}
              onSelect={(selection) => {
                modalManager.close();
                onSelect(selection, connectFrom);
              }}
              onAddTrigger={() => {
                modalManager.close();
                onAddTrigger();
              }}
              onCreateAgent={() => {
                modalManager.close();
                onCreateAgent();
              }}
              onCreateWorkflow={() => {
                modalManager.close();
                onCreateWorkflow();
              }}
              onDismiss={() => modalManager.close()}
            />
          </ModalPageView>
        ),
      });
    },
    [
      canAddTrigger,
      defaultConnectFrom,
      excludeWorkflowId,
      intent,
      modalManager,
      onAddTrigger,
      onCreateAgent,
      onCreateWorkflow,
      onSelect,
    ]
  );
};
